import React, { useState, useEffect } from "react";
import { Phone, Mail, CheckCircle2, AlertCircle } from "lucide-react";
import enquiriesService from "./services/enquiriesService";
import settingsService from "./services/settingsService";

function Contact() {
  const [formData, setFormData] = useState({
    name: "",
    phone: "",
    email: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState(null); // 'success' | 'error' | null
  const [statusMessage, setStatusMessage] = useState("");

  const [contactSettings, setContactSettings] = useState({
    phone: "+91 87545 45419",
    phone_raw: "918754545419",
    email: "vjdsignstudio@gmail.com",
    address: "Tiruppur, Tamil Nadu",
    instagram_url: "https://www.instagram.com/vjdsignstudio/",
    facebook_url: "https://www.facebook.com/share/16xZtiEpY5/",
  });

  useEffect(() => {
    async function loadContactSettings() {
      try {
        const general = await settingsService.getSetting("general");
        const all = await settingsService.getAll();
        if (general || all) {
          const rawPhone = (general?.phone || all?.phone || "").replace(/[^0-9]/g, "");
          setContactSettings((prev) => ({
            ...prev,
            phone: general?.phone || all?.phone || prev.phone,
            phone_raw: rawPhone || prev.phone_raw,
            email: general?.email || all?.email || prev.email,
            address: general?.address || all?.address || prev.address,
            instagram_url: general?.instagram_url || all?.instagram_url || prev.instagram_url,
            facebook_url: general?.facebook_url || all?.facebook_url || prev.facebook_url,
          }));
        }
      } catch (err) {
        // Fallback safely
      }
    }
    loadContactSettings();
  }, []);

  const contactInfo = [
    {
      icon: <Phone className="w-6 h-6 sm:w-7 sm:h-7" />,
      title: "Call Us",
      value: contactSettings.phone,
      link: `tel:${contactSettings.phone_raw || "+918754545419"}`,
    },
    {
      icon: <Mail className="w-6 h-6 sm:w-7 sm:h-7" />,
      title: "Email Us",
      value: contactSettings.email,
      link: `mailto:${contactSettings.email}`,
    },
  ];

  const socials = [
    {
      name: "Instagram",
      icon: (
        <svg 
          className="w-5 h-5 fill-none stroke-current" 
          viewBox="0 0 24 24" 
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round"
        >
          <rect width="20" height="20" x="2" y="2" rx="5" ry="5"/>
          <path d="M16 11.37A4 4 0 1 1 12.63 8 4 4 0 0 1 16 11.37zM17.5 6.5h.01"/>
        </svg>
      ),
      link: contactSettings.instagram_url,
      color: "hover:bg-gradient-to-tr hover:from-yellow-500 hover:to-purple-600 hover:text-white",
    },
    {
      name: "Facebook",
      icon: (
        <svg 
          className="w-5 h-5 fill-none stroke-current" 
          viewBox="0 0 24 24" 
          strokeWidth="2" 
          strokeLinecap="round" 
          strokeLinejoin="round"
        >
          <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z"/>
        </svg>
      ),
      link: contactSettings.facebook_url,
      color: "hover:bg-[#1877F2] hover:text-white",
    },
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitStatus(null);
    setStatusMessage("");

    if (!formData.name.trim() || !formData.phone.trim() || !formData.email.trim() || !formData.message.trim()) {
      setSubmitStatus("error");
      setStatusMessage("Please fill in all the required fields.");
      return;
    }

    try {
      setIsSubmitting(true);
      await enquiriesService.submitEnquiry({
        name: formData.name,
        phone: formData.phone,
        email: formData.email,
        subject: "Website Contact Form Inquiry",
        message: formData.message,
      });

      setSubmitStatus("success");
      setStatusMessage("Thank you! Your inquiry has been sent to VJ Dsign Studio. Our design team will contact you shortly.");
      setFormData({ name: "", phone: "", email: "", message: "" });
    } catch (err) {
      console.error("Enquiry submission error:", err);
      setSubmitStatus("error");
      setStatusMessage(err.message || "Failed to send inquiry. Please try again or reach us via WhatsApp.");
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section
      id="contact"
      className="relative overflow-hidden py-16 md:py-24 bg-gradient-to-b from-white via-slate-50/50 to-white"
    >
      <div className="absolute inset-0 overflow-hidden pointer-events-none z-0">
        <div className="absolute -top-20 -left-20 w-72 sm:w-[450px] h-72 sm:h-[450px] bg-[#9BB7A3]/10 rounded-full blur-[80px] sm:blur-[120px]"></div>
        <div className="absolute -bottom-20 -right-20 w-72 sm:w-[500px] h-72 sm:h-[500px] bg-slate-200/60 rounded-full blur-[90px] sm:blur-[140px]"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-12 md:mb-16">
          <span className="text-[#9BB7A3] font-bold tracking-[3px] sm:tracking-[4px] uppercase text-xs sm:text-sm">
            Get In Touch
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-5xl font-extrabold text-slate-800 mt-3 sm:mt-4 px-2">
            Let's Craft Your Dream Space
          </h2>
          <div className="w-16 sm:w-24 h-1 bg-[#9BB7A3] mx-auto mt-4 sm:mt-6 rounded-full"></div>
        </div>

        <div className="grid lg:grid-cols-5 gap-8 md:gap-12 items-start">
          
          {/* Left Side: Contact Cards */}
          <div className="lg:col-span-2 space-y-4 sm:space-y-5">
            {contactInfo.map((item, index) => (
              <a
                href={item.link}
                key={index}
                className="
                  flex 
                  items-center 
                  gap-4 sm:gap-5 
                  bg-white/80 
                  backdrop-blur-md 
                  p-5 sm:p-6 
                  rounded-2xl 
                  border border-slate-100 
                  shadow-sm 
                  hover:shadow-md 
                  hover:border-[#9BB7A3]/40 
                  -translate-y-0 hover:-translate-y-1
                  transition-all 
                  duration-300
                  group
                "
              >
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-xl bg-[#9BB7A3]/10 flex items-center justify-center text-[#9BB7A3] group-hover:scale-105 transition-transform duration-300">
                  {item.icon}
                </div>
                <div className="text-left min-w-0">
                  <h4 className="text-xs sm:text-sm font-bold text-slate-400 uppercase tracking-wider">
                    {item.title}
                  </h4>
                  <p className="text-base sm:text-lg font-bold text-slate-800 mt-0.5 truncate">
                    {item.value}
                  </p>
                </div>
              </a>
            ))}

            {/* Social Media Connect Box */}
            <div className="bg-white/80 backdrop-blur-md p-5 sm:p-6 rounded-2xl border border-slate-100 shadow-sm text-center sm:text-left">
              <h4 className="text-sm font-bold text-slate-800 mb-4">
                Connect with VJ Dsign Studio
              </h4>
              <div className="flex items-center justify-center sm:justify-start gap-3">
                {socials.map((social, index) => (
                  <a
                    href={social.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    key={index}
                    title={social.name}
                    className="
                      w-10 h-10 
                      rounded-xl 
                      bg-slate-50 
                      border border-slate-200/60 
                      text-slate-600 
                      flex items-center justify-center 
                      transition-all duration-300 
                      hover:scale-105
                      active:scale-95
                    "
                  >
                    <span className={`w-full h-full rounded-xl flex items-center justify-center transition-all duration-300 ${social.color}`}>
                      {social.icon}
                    </span>
                  </a>
                ))}
              </div>
            </div>
          </div>

          {/* Right Side: Contact Form */}
          <div className="lg:col-span-3 bg-white/80 backdrop-blur-md p-6 sm:p-8 rounded-3xl border border-slate-100 shadow-sm">
            <h3 className="text-xl font-bold text-slate-800 text-left mb-6">
              Send Us a Message
            </h3>
            
            {submitStatus === "success" && (
              <div className="mb-6 p-4 rounded-2xl bg-emerald-50 border border-emerald-200 text-emerald-800 text-sm font-medium flex items-start gap-3 animate-fadeIn">
                <CheckCircle2 size={20} className="text-emerald-600 shrink-0 mt-0.5" />
                <p className="leading-snug">{statusMessage}</p>
              </div>
            )}

            {submitStatus === "error" && (
              <div className="mb-6 p-4 rounded-2xl bg-rose-50 border border-rose-200 text-rose-800 text-sm font-medium flex items-start gap-3 animate-fadeIn">
                <AlertCircle size={20} className="text-rose-600 shrink-0 mt-0.5" />
                <p className="leading-snug">{statusMessage}</p>
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="flex flex-col text-left">
                  <label className="text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-wider">Your Name *</label>
                  <input
                    type="text"
                    required
                    placeholder="John Doe"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl bg-white border border-slate-200 text-sm focus:outline-none focus:border-[#9BB7A3] transition-colors"
                  />
                </div>
                <div className="flex flex-col text-left">
                  <label className="text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-wider">Phone Number *</label>
                  <input
                    type="tel"
                    required
                    placeholder="+91 98765 43210"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 rounded-xl bg-white border border-slate-200 text-sm focus:outline-none focus:border-[#9BB7A3] transition-colors"
                  />
                </div>
              </div>

              <div className="flex flex-col text-left">
                <label className="text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-wider">Email Address *</label>
                <input
                  type="email"
                  required
                  placeholder="john@example.com"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-white border border-slate-200 text-sm focus:outline-none focus:border-[#9BB7A3] transition-colors"
                />
              </div>

              <div className="flex flex-col text-left">
                <label className="text-xs font-bold text-slate-500 mb-1.5 uppercase tracking-wider">Message *</label>
                <textarea
                  rows="4"
                  required
                  placeholder="Tell us about your interior requirements, property location, timeline..."
                  value={formData.message}
                  onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                  className="w-full px-4 py-3 rounded-xl bg-white border border-slate-200 text-sm focus:outline-none focus:border-[#9BB7A3] transition-colors resize-none"
                ></textarea>
              </div>

              <button
                type="submit"
                disabled={isSubmitting}
                className="
                  w-full 
                  py-3.5 
                  px-6 
                  bg-[#9BB7A3] 
                  hover:bg-[#8aa892] 
                  text-white 
                  font-bold 
                  rounded-xl 
                  shadow-md 
                  hover:shadow-lg 
                  transition-all 
                  duration-300 
                  text-sm 
                  uppercase 
                  tracking-wider
                  mt-2
                  disabled:opacity-50
                  flex items-center justify-center gap-2
                "
              >
                {isSubmitting ? (
                  <>
                    <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                    <span>Submitting Inquiry...</span>
                  </>
                ) : (
                  <span>Submit Inquiry</span>
                )}
              </button>
            </form>
          </div>

        </div>
      </div>
    </section>
  );
}

export default Contact;